/**
 * WordSelector - Word selection logic service
 * Handles random and daily word selection
 */

import { WordRepository } from '../repositories/WordRepository';
import { GAME_CONFIG } from '../config';

export interface WordSelectionResult {
  word: string;
  length: number;
  isDaily: boolean;
}

export class WordSelector {
  private wordRepository: WordRepository;

  constructor(wordRepository: WordRepository) {
    this.wordRepository = wordRepository;
  }

  /**
   * Select a word for free play mode
   * This matches the original random word selection logic
   * 
   * @param length - Desired word length
   * @returns Selected word and metadata
   */
  selectFreePlayWord(length: number = GAME_CONFIG.defaultWordLength): WordSelectionResult {
    // Validate length
    if (length < GAME_CONFIG.minWordLength || length > GAME_CONFIG.maxWordLength) {
      length = GAME_CONFIG.defaultWordLength;
    }

    const word = this.wordRepository.getRandomWordForLength(length);
    
    return {
      word,
      length,
      isDaily: false
    };
  }

  /**
   * Select the daily word for today
   * This uses the deterministic daily word selection from WordRepository
   * 
   * @param length - Current word length for daily progression
   * @returns Selected daily word and metadata
   */
  selectDailyWord(length: number): WordSelectionResult {
    // Validate length for daily mode
    if (length < GAME_CONFIG.minWordLength || length > GAME_CONFIG.maxWordLength) {
      length = GAME_CONFIG.minWordLength; // Start daily progression at minimum length
    }

    const word = this.wordRepository.getDailyWord(length);
    
    return {
      word,
      length,
      isDaily: true
    };
  }

  /**
   * Get the next word length for daily progression
   * Daily mode progresses: 5 → 6 → 7 → 8 → 9 → 10
   * 
   * @param currentLength - Current word length
   * @returns Next length in progression, or null if completed
   */
  getNextDailyLength(currentLength: number): number | null {
    if (currentLength >= GAME_CONFIG.maxWordLength) {
      return null; // Daily challenge completed
    }
    
    return currentLength + 1;
  }

  /**
   * Check if daily progression is complete
   * 
   * @param currentLength - Current word length
   * @returns True if all daily words completed
   */
  isDailyProgressionComplete(currentLength: number): boolean {
    return currentLength >= GAME_CONFIG.maxWordLength;
  }

  /**
   * Get available word lengths
   * 
   * @returns Array of valid word lengths
   */
  getAvailableLengths(): number[] {
    const lengths: number[] = [];
    for (let i = GAME_CONFIG.minWordLength; i <= GAME_CONFIG.maxWordLength; i++) {
      lengths.push(i);
    }
    return lengths;
  }

  /**
   * Validate word length
   * 
   * @param length - Length to validate
   * @returns True if valid
   */
  isValidLength(length: number): boolean {
    return length >= GAME_CONFIG.minWordLength && length <= GAME_CONFIG.maxWordLength;
  }

  // ===== WORD PACK SUPPORT (FREE PLAY MODE ONLY) =====
  // Daily mode continues to use selectDailyWord() method above

  /**
   * Select a word from a word pack for free play mode
   * This replaces length-based selection with pack-based selection
   * 
   * @param packId - ID of the word pack to select from
   * @param fallbackLength - Word length to use if pack fails (defaults to game default)
   * @returns Selected word and metadata
   */
  selectFreePlayWordFromPack(packId: string, fallbackLength: number = GAME_CONFIG.defaultWordLength): WordSelectionResult {
    try {
      // Validate pack exists
      const isValid = this.wordRepository.isValidWordPack(packId);
      if (!isValid) {
        throw new Error(`Invalid or missing word pack: ${packId}`);
      }

      // Get random word from pack
      const word = this.wordRepository.getRandomWordFromPack(packId);
      
      return {
        word,
        length: word.length,
        isDaily: false
      };
    } catch (error) {
      console.error('Error selecting word from pack:', error);
      // Fallback to requested length-based selection if pack fails
      console.log(`Falling back to length-based word selection (length: ${fallbackLength})`);
      return this.selectFreePlayWord(fallbackLength);
    }
  }
}
